import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Upload, X, FileText, Image, Video, AlertTriangle, Trash2 } from 'lucide-react';
import FileUploadZone from '../components/features/upload/FileUploadZone';
import AISettings from '../components/features/upload/AISettings';
import CreditValidationModal from '../components/features/upload/CreditValidationModal';

import { useUpload } from '../hooks/upload/useUpload';
import { getFileBreakdown } from '../utils/creditCalculation';
import { useUserData } from '../hooks/useUserData';


interface ErrorMessage {
  id: string;
  message: string;
  type: 'error' | 'warning';
}

const UploadPage: React.FC = () => {
  // Truncate filename for display (frontend only - DB keeps original)
  const truncateFilename = (filename: string, maxLength: number = 28) => {
    if (filename.length <= maxLength) return filename;
    
    // Find the last dot for extension
    const lastDotIndex = filename.lastIndexOf('.');
    
    if (lastDotIndex === -1) {
      // No extension, just truncate
      return filename.slice(0, maxLength - 3) + '...';
    }
    
    const extension = filename.slice(lastDotIndex);
    const nameWithoutExt = filename.slice(0, lastDotIndex);
    
    // Calculate available space for name (total - extension - ellipsis)
    const availableLength = maxLength - extension.length - 3;
    
    if (availableLength < 1) {
      // Extension too long, just show first part
      return filename.slice(0, maxLength - 3) + '...';
    }
    
    return nameWithoutExt.slice(0, availableLength) + '...' + extension;
  };

  // 💫 CRITICAL: Clean up old album localStorage entries
  // This prevents localStorage bloat while preserving active album data for multi-tab support
  useEffect(() => {
  // ...existing code...
    
    // Clean up old non-album-specific storage (legacy)
    localStorage.removeItem('pendingUploadData');
    
    // Clean up old album-specific entries (older than 24 hours)
    const activeAlbums = JSON.parse(localStorage.getItem('activeAlbums') || '[]');
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    const validAlbums: string[] = [];
    
    activeAlbums.forEach((albumSlug: string) => {
      const albumKey = `pendingUploadData_${albumSlug}`;
      const albumData = localStorage.getItem(albumKey);
      
      if (albumData) {
        try {
          const parsed = JSON.parse(albumData);
          const age = now - (parsed.timestamp || 0);
          
          if (age > maxAge) {
            // Remove old album data
            // ...existing code...
            localStorage.removeItem(albumKey);
          } else {
            // Keep valid album
            validAlbums.push(albumSlug);
          }
        } catch (error) {
          // Remove corrupted data
          // ...existing code...
          localStorage.removeItem(albumKey);
        }
      }
    });
    
    // Update active albums list
    localStorage.setItem('activeAlbums', JSON.stringify(validAlbums));
    
  // ...existing code...
  }, []); // Only run once on component mount

  const {
    files,
    invalidFiles,
    filesWithWarnings,
    duplicateFiles,
    isDragOver,
    isValidating,
    isUploading,
    isCreditValidating,
    showCreditModal,
    creditInfo,
    creditValidationResult,
    uploadStatus,
    albumCreationResult,

    aiSettings,
    uploadErrors,
    validationErrors,
    systemErrors,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInput,
    handleClearAll,
    handleClearErrors,
    handleRemoveError,
    handleRemoveFile,
    handleSettingsChange,
  handleResetSettings,
  handleUpload,
  handleCreateAlbum,
  handleCloseCreditModal
  } = useUpload();

  // Use the SAME REUSABLE hook - NO MORE DUPLICATE HOOKS!
  const { userInfo } = useUserData();
  const availableCredits = userInfo?.credits || 0;
  
  // Combine credit info with actual balance
  const finalCreditInfo = {
    ...creditInfo,
    balance: availableCredits
  };

  // Calculate file breakdown for modal display
  const fileBreakdown = getFileBreakdown(files.map(uploadedFile => ({
    name: uploadedFile.file.name,
    type: uploadedFile.file.type,
    size: uploadedFile.file.size
  })));

  // Create error messages from validation results
  const createErrorMessages = (): ErrorMessage[] => {
    const errorMessages: ErrorMessage[] = [];
      
    // Add backend validation errors (Layer 2) - HIGHEST PRIORITY
    uploadErrors.forEach((error, index) => {
      errorMessages.push({
        id: `upload-error-${Date.now()}-${index}`,
        message: `${error}`,
        type: 'error'
      });
    });
    
    validationErrors.forEach((error, index) => {
      errorMessages.push({
        id: `validation-error-${Date.now()}-${index}`,
        message: `${error}`,
        type: 'error'
      });
    });
    
    systemErrors.forEach((error, index) => {
      errorMessages.push({
        id: `system-error-${Date.now()}-${index}`,
        message: `${error}`,
        type: 'error'
      });
    });
      
    // Add individual file errors (Frontend Layer 1)
    invalidFiles.forEach((invalidFile, index) => {
      invalidFile.errors.forEach((error, errorIndex) => {
        errorMessages.push({
          id: `file-${Date.now()}-${index}-${errorIndex}`,
          message: `${error}`,
          type: 'error'
        });
      });
    });
    
    // Add file warnings
    filesWithWarnings.forEach((fileWithWarning, index) => {
      fileWithWarning.warnings.forEach((warning, warningIndex) => {
        errorMessages.push({
          id: `warning-${Date.now()}-${index}-${warningIndex}`,
          message: `⚠️ ${warning}`,
          type: 'warning'
        });
      });
    });
    
    // Add duplicate file warnings
    duplicateFiles.forEach((dupFile, index) => {
      errorMessages.push({
        id: `dup-${Date.now()}-${index}`,
        message: `⚠️ Duplicate detected: ${dupFile.name}. Only one copy has been kept, the rest were removed.`,
        type: 'warning'
      });
    });
    
    return errorMessages;
  };
  
  const errorMessages = createErrorMessages();

  // Calculate total file size
  const totalFileSize = files.reduce((total, file) => total + file.file.size, 0);
  const totalFileSizeMB = (totalFileSize / 1024 / 1024).toFixed(2);





















  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Desktop App Promo Banner - Mobile Friendly */}
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-start sm:items-center gap-3 flex-1">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Need to process more images and videos? Try our Desktop App
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Process larger batches with better performance • Available for macOS & Windows
                  </p>
                </div>
              </div>
              <a
                href="/desktop"
                className="w-full sm:w-auto flex-shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </a>
            </div>
          </div>

          {/* Main Content - 2 Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Upload Files */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                Upload Your Files
              </h2>

              {/* File Upload Zone with Early Warning Validation */}
              <FileUploadZone
                 onDrop={handleDrop}
                 onFileInput={handleFileInput}
                 isDragOver={isDragOver}
                 onDragOver={handleDragOver}
                 onDragLeave={handleDragLeave}
                 isValidating={isValidating}
               />

              {/* Error Messages */}
              {errorMessages.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <AlertTriangle className="w-5 h-5 mr-2 text-amber-500" />
                      Validation Messages ({errorMessages.length})
                    </h3>
                    <button
                      onClick={handleClearErrors}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {errorMessages.map((error) => (
                      <div
                        key={error.id}
                        className={`flex items-start justify-between p-3 rounded-lg border ${
                          error.type === 'error'
                            ? 'bg-red-50 border-red-200 text-red-800'
                            : 'bg-amber-50 border-amber-200 text-amber-800'
                        }`}
                      >
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            error.type === 'error' ? 'text-red-500' : 'text-amber-500'
                          }`} />
                          <p className="text-sm">{error.message}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveError(error.id)}
                          className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Queue */}
              {files.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        File Queue ({files.length}) - {totalFileSizeMB} MB
                      </h3>
                      <button
                        onClick={handleClearAll}
                        className="text-sm text-gray-500 hover:text-red-600 flex items-center transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Clear All
                      </button>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {files.map((file) => (
                       <div
                         key={file.id}
                         className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                       >
                         <div className="flex items-center space-x-3">
                           <div className="text-gray-500">
                             {file.file.type.startsWith('image/') ? <Image className="w-4 h-4" /> : 
                              file.file.type.startsWith('video/') ? <Video className="w-4 h-4" /> : 
                              <FileText className="w-4 h-4" />}
                           </div>
                           <div className="flex-1 min-w-0">
                             <p className="text-sm font-medium text-gray-900 truncate" title={file.file.name}>
                               {truncateFilename(file.file.name)}
                             </p>
                             <p className="text-xs text-gray-500">
                               {(file.file.size / 1024 / 1024).toFixed(2)} MB
                             </p>
                           </div>
                         </div>
                        <button
                           onClick={() => handleRemoveFile(file.id)}
                           className="text-gray-400 hover:text-red-500 transition-colors"
                         >
                           <X className="w-4 h-4" />
                         </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>



            {/* Right Column - AI Settings */}
            <AISettings
              settings={aiSettings}
              onSettingsChange={handleSettingsChange}
              onResetSettings={handleResetSettings}
              filesCount={files.length}
              onUpload={handleUpload}
              isLoading={isCreditValidating}
              showCreditModal={showCreditModal}
            />
          </div>
        </div>
      </div>
      
      {/* Credit Validation Modal */}
      <CreditValidationModal
        isOpen={showCreditModal}
        onClose={handleCloseCreditModal}
        onConfirm={handleCreateAlbum}
        requiredCredits={finalCreditInfo.required}
        availableCredits={availableCredits}
        filesCount={files.length}
        fileBreakdown={fileBreakdown}
        isValidating={isUploading}
        creditValidationResult={creditValidationResult}
        uploadStatus={uploadStatus}
        albumCreationResult={albumCreationResult}
        // New props for membership validation
        hasActiveMembership={userInfo?.current_package && userInfo?.membership_expires_at && new Date(userInfo.membership_expires_at) > new Date()}
        isFreeAccount={userInfo?.free_account_status || false}
        membershipExpired={userInfo?.current_package && userInfo?.membership_expires_at && new Date(userInfo.membership_expires_at) <= new Date()}
        currentPackage={userInfo?.current_package}
        membershipExpiresAt={userInfo?.membership_expires_at}
      />
      
      {/* Development Tool: Layer 1 Validation Toggle */}
      
      
    </Layout>
  );
};

export default UploadPage;